import { AttendanceChannel, Attendant, ContactChannel } from './attendance';
import { BotFrameworkAttachments } from './bot-framework.interface';

export interface AttendanceMessageContent {
  text?: string;
  new?: any;
  old?: any;
  eventName?: string;
  variables?: any;
  contactChannel?: ContactChannel;
  attachments?: Array<BotFrameworkAttachments>;
  media?: {
    fn?: string;
    url?: string;
    type?: AttendanceContentType;
    caption?: string;
    mediaText?: string;
    mimetype?: string;
    isUploading?: boolean;
    available?: boolean;
    /** Marcado pelo generic-bot no envio de cotação (PDF ou imagem). */
    isPriceQuote?: boolean;
    button?: {
      label: string;
      action: string;
      isLink?: boolean;
    };
  };
  isAskFlow?: boolean;
  /** WhatsApp coexistence: set by the BFF on the "WhatsApp Web" intervention event. */
  isCoex?: boolean;
  contacts?: [Contacts];
  createdAt?: string;
  value?: {
    formData?: Array<AttendanceContentFormData>;
  };
  gallery?: Array<CarouselItem>;
  reply?: {
    content:
      | MediaReply
      | ContactReply
      | TextReply
      | UnsupportedReply
      | StoryReply
      | LocationReply;
    type: MediaReplyType;
    name: string;
    status?: AttendanceMessageStatus;
  };
  location?: {
    mapUrl: string;
    address: string;
    latitude: string;
    longitude: string;
    mapImageUrl: string;
  };
  isUnsupported?: boolean;
  messageInfo?: EmailMessageInfo;
  eventType?: string,
  attendant?: Attendant;
  voiceCallChannel?: any;
  canBeReplied?: boolean;
  platformReferenceId?: string;
  name?: string;
  foundPhoneNumbers?: string[];
  metaReferral?: MetaReferral;
}

export interface MetaReferral {
  channel: string;
  // whatsapp (click-to-whatsapp ads)
  headline?: string;
  source_type?: string; // 'ad'
  source_id?: string;
  source_url?: string;
  media_type?: 'image' | 'video';
  image_url?: string;
  thumbnail_url?: string;
  ctwa_clid?: string;
  // instagram ads
  type?: string; // 'OPEN_THREAD'
  ad_id?: string;
  source?: string; // 'ADS'
  ads_context_data?: {
    ad_title?: string;
    video_url?: string;
    photo_url?: string;
  };
}

export type MediaReplyType =
  | 'video'
  | 'image'
  | 'text'
  | 'audio'
  | 'contact'
  | 'document'
  | 'image_attachment'
  | 'share'
  | 'ig_story_reply'
  | 'list_buttons';

export type MediaReply = {
  media:
    | {
        type: 'video';
        mimetype: string;
        url: string;
        fn?: string;
      }
    | {
        type: 'image';
        url: string;
        caption?: string;
        fn?: string;
      }
    | {
        type: 'audio';
        url: string;
        fn?: string;
      }
    | {
        type: 'document';
        url: string;
        caption: string;
      }
    | {
        type: 'image_attachment';
        url: string;
      }
    | {
        type: 'share';
        url: string;
      };
};

export type TextReply = { text: string };

export type UnsupportedReply = { isUnsupported: boolean };

export type ContactReply = {
  contacts: {
    fullName: string;
  }[];
};

export type StoryReply = {
  story: {
    url: string;
    type: 'video' | 'image';
    mimetype: string;
  };
};

export type LocationReply = {
  location: {
    mapUrl: string;
    latitude: string;
    longitude: string;
    mapImageUrl?: string;
  };
};

export interface AttendanceContentFormData {
  id: string;
  label: string;
  type: string;
  value: string;
}

export interface CarouselItem {
  image_url?: string;
  title?: string;
  subtitle?: string;
  text?: string;
  buttons?: Array<CarouselButton>;
}

export interface CarouselButton {
  payload: string;
  title: string;
  type: string;
}

export interface MetaData{
  transcription?: string
  code?: number | string
  reason?: string
  platform?: string
}

export type AttendanceMessage = (
  | {
      type: AttendanceMessageType.MESSAGE;
      channel: AttendanceChannel;
      attendant?: Attendant | null;
      status?: AttendanceMessageStatus;
      data: {
        type: AttendanceContentType;
        content: AttendanceMessageContent;
        status: AttendanceMessageStatus;
        isSender: boolean;
        canBeReplied?: boolean;
        intentTrackId?: bigint;
        intentTrackResolver?: string;
      };
      metadata?: MetaData
    }
  | {
      type: AttendanceMessageType.EVENT;
      data: {
        type: AttendanceEventType;
        content: any;
        status?: AttendanceMessageStatus | null;
        intentTrackId?: bigint;
        intentTrackResolver?: string;
      };
      channel?: AttendanceChannel;
    }
) & {
  id?: string;
  frontendMessageId?: string;
  attendanceId?: string;
  createdAt: string;
  platformTimestamp?: string | null;
  attendant?: Attendant
};


export type HistoriesWithEvent = {
  histories: AttendanceMessage[][];
  event: EventTypeHistory;
}

export enum EventTypeHistory  {
  NEW_MESSAGE = 'new-message',
  OLD_MESSAGES = 'old-messages',
  UPDATE_MESSAGE = 'update-message'
}

export enum AttendanceMessageStatus {
  UNTRACKED = 'untracked', // Not tracked
  AWAITING = 'awaiting', // Processing from backend
  SENT = 'sent', // Accepted by the server
  DELIVERED = 'delivered', // Delivered to user
  READ = 'read', // Read by the user
  FAILED = 'failed', // Message was not delivered to the user
  DELETED = 'deleted', // Message deleted - instagram
}

export enum AttendanceMessageType {
  MESSAGE = 'message',
  EVENT = 'event',
}

export enum AttendanceContentType {
  TEXT = 'text',
  VIDEO = 'video',
  IMAGE = 'image',
  STICKER = 'sticker',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  FORM = 'form',
  IMAGE_ATTACHMENT = 'image_attachment',
  CAROUSEL = 'carousel',
  LOCATION = 'location',
  CONTACT = 'contact',
  LIST_BUTTONS = 'list_buttons',
  CARD = 'card',
  STORY_REPLY = 'ig_story_reply',
  STORY_MENTION = 'ig_story_mention',
  SHARE = 'share',
  SHAREPOST = 'share_post',
  REELS = 'reels',
  EMAIL = 'email',
  PHONE = 'phone'
}

export enum AttendanceEventType {
  CHANGE_COMPANY = 'change_company',
  CHANGE_CHANNEL = 'change_channel',
  CHANGE_CHANNEL_CHAT_WEB_UNAVAILABLE = 'change_channel_chat_web_unavailable',
  CHANGE_ATTENDANT_BOT_ACTIVATED = 'change_attendant_bot_activated',
  CHANGE_ATTENDANT_BOT_STOPPED = 'change_attendant_bot_stopped',
  CHANGE_ASSIGNED_ATTENDANT_AUTOMATICALLY = 'change_assigned_attendant_automatically',
  CHANGE_ASSIGNED_ATTENDANT_MANUALLY = 'change_assigned_attendant_manually',
  CHANGE_ASSIGNED_ATTENDANT_MANUALLY_SELF = 'change_assigned_attendant_manually_self',
  CHANGE_ASSIGNED_ATTENDANT_MANUALLY_GROUP = 'change_assigned_attendant_manually_group',
  ACTION_AVAILABLE_CONTACT_CHANNEL_WEBSITE = 'action_available_contact_channel_website',
  ACTION_UNAVAILABLE_CONTACT_CHANNEL_WEBSITE = 'action_unavailable_contact_channel_website',
  ACTION_LINK_QUOTATION_OPENED = 'action_link_quotation_opened',
  ACTION_MERGE_ATTENDANCE = 'action_merge_attendance',
  ACTION_RESERVATION_MANUALLY_CONFIRMED = 'action_reservation_manually_confirmed',
  ACTION_RESERVATION_MANUALLY_CONFIRMED_TRAVELER = 'action_reservation_manually_confirmed_traveler',
  ACTION_RESERVATION_MANUALLY_CONFIRMED_TRAVELER_NO_HOTEL_ID = 'action_reservation_manually_confirmed_traveler_no_hotel_id',
  ACTION_RESERVATION_MANUALLY_CONFIRMED_GROUP = 'action_reservation_manually_confirmed_group',
  ACTION_RESERVATION_MANUALLY_CREATED = 'action_reservation_manually_created',
  ACTION_RESERVATION_MANUALLY_CREATED_GROUP = 'action_reservation_manually_created_group',
  ACTION_RESERVATION_AUTOMATICALLY_CREATED = 'action_reservation_automatically_created',
  ACTION_RESERVATION_MANUALLY_UPDATED = 'action_reservation_manually_updated',
  ACTION_RESERVATION_MANUALLY_UPDATED_GROUP = 'action_reservation_manually_updated_group',
  ACTION_RESERVATION_AUTOMATICALLY_UPDATED = 'action_reservation_automatically_updated',
  ACTION_RESERVATION_MANUALLY_CANCELED = 'action_reservation_manually_canceled',
  ACTION_RESERVATION_AUTOMATICALLY_CANCELED = 'action_reservation_automatically_canceled',
  ACTION_RESERVATION_AUTOMATICALLY_BOOKING = 'action_reservation_automatically_booking',
  ACTION_RESERVATION_AUTOMATICALLY_EXPEDIA = 'action_reservation_automatically_expedia',
  ACTION_UNAVAILABLE_CONTACT_CHANNEL = 'action_unavailable_contact_channel',
  CHANGE_ASSIGNED_ATTENDANT_SELF = 'change_assigned_attendant_self',
  CHANGE_ATTENDANT_BOT_ACTIVATED_AUTOMATICALLY = 'change_attendant_bot_activated_automatically',
  CHANGE_ATTENDANT_BOT_ACTIVATED_MANUALLY = 'change_attendant_bot_activated_manually',
  CHANGE_ATTENDANT_BOT_STOPPED_AUTOMATICALLY = 'change_attendant_bot_stopped_automatically',
  CHANGE_ATTENDANT_BOT_STOPPED_MANUALLY = 'change_attendant_bot_stopped_manually',
  CHANGE_ATTENDANT_MANUALLY = 'change_attendant_manually',
  CHANGE_STATUS_ARCHIVED_AUTOMATICALLY = 'change_status_archived_automatically',
  CHANGE_STATUS_CLOSED_AUTOMATICALLY = 'change_status_closed_automatically',
  CHANGE_STATUS_CLOSED_AUTOMATICALLY_CONVERSATION_EXPIRED = 'change_status_closed_automatically_conversation_expired',
  CHANGE_STATUS_CLOSED_MANUALLY = 'change_status_closed_manually',
  CHANGE_STATUS_OPENED_AUTOMATICALLY = 'change_status_opened_automatically',
  CHANGE_STATUS_OPENED_MANUALLY = 'change_status_opened_manually',
  START_ATTENDANCE = 'start_attendance',
  CHANGE_ATTENDANT_BOT_STOPPED_BY_OUTER_MANUALLY = 'change_attendant_bot_stopped_by_outer_manually',
  CHANGE_ATTENDANT_BOT_STOPPED_BY_OUTER_MANUALLY_INSTAGRAM = 'change_attendant_bot_stopped_by_outer_manually_instagram',
  CHANGE_ATTENDANT_BOT_STOPPED_BY_OUTER_MANUALLY_WHATSAPP = 'change_attendant_bot_stopped_by_outer_manually_whatsapp',
  CHANGE_ATTENDANT_BOT_STOPPED_BY_OUTER_MANUALLY_FACEBOOK = 'change_attendant_bot_stopped_by_outer_manually_facebook',
  CHANGE_ATTENDANT_BOT_STOPPED_BY_CAMPAIGN = 'change_attendant_bot_stopped_by_campaign',
  CHANGE_ATTENDANT_BOT_STOPPED_BY_SPAM = 'change_attendant_bot_stopped_by_spam',
  CHANGE_ATTENDANT_BOT_STOPPED_BY_TAG_AUTOMATION = 'change_attendant_bot_stopped_by_tag_automation',
  OLD_HISTORY_LINK = 'old_history_link',
  NEXT_ATTENDANCE_LINK = 'next_attendance_link',
  PREVIOUS_ATTENDANCE_LINK = 'previous_attendance_link',
  NEXT_ATTENDANCE_SKIPPED = 'next_attendance_skipped',
  PREVIOUS_ATTENDANCE_SKIPPED = 'previous_attendance_skipped',
  ACTION_FROM_MERGE_ATTENDANCE = "action_from_merge_attendance",
  ACTION_PHONE_TRANSFER_DATA = "action_phone_transfer_data",
  ACTION_QUOTATION = 'action_quotation',
  ACTION_QUOTATION_UNAVAILABILITY = 'action_quotation_unavailability',
  ACTION_FREE_ENTRY_POINT_STARTED = 'action_free_entry_point_started',
  ACTION_FREE_ENTRY_POINT_ARCHIVED = 'action_free_entry_point_archived',
  ACTION_FREE_ENTRY_POINT_REMAINING = 'action_free_entry_point_remaining',
}

export interface AttendanceListHistoryParams {
  attendanceId: string;
  previousAttendanceId?: string;
  companyId: string;
  itemsPerPage: number;
  page: number;
  channelReferenceId: string;
  channel: AttendanceChannel;
  cachedHasUnreadMessages?: boolean;
  cachedLastContactEventAt?: string;
}

export interface AttendanceListOldestHistoryParams {
  lastCreatedAt: string;
  channel: AttendanceChannel;
  channelReferenceId: string;
  companyId: string;
}

export interface AttendanceHistoryResponse {
  attendanceHistory: AttendanceMessage[];
  userState: {
    id: string;
    userId: string;
    connectionId?: string;
    filters: {
      page: string;
      tags: any[];
      search: string;
      status: string[];
      endDate: string;
      channels: any[];
      startDate: string;
      companiesIds: string[];
      itemsPerPage: string;
      currentAttendant: string;
      assignedAttendant: string;
      hasUnreadMessages: boolean;
      emailHasAttachment: boolean;
    };
    attendanceList: string[];
    attendanceSelectedId: string;
  };
  shouldCheckForOldestHistory?: boolean;
  enableSophia: boolean;
}

export interface Contacts {
  firstName: string;
  fullName: string;
  lastName: string;
  phones: [
    {
      type: string;
      phone: string;
    }
  ];
}

export interface EmailMessageInfo {
  cc?: EmailMessageRecipient[];
  to: EmailMessageRecipient[];
  from: EmailMessageRecipient[];
  files: EmailMessageFile[];
  subject: string;
  threadId: string;
  messageId: string;
  totalThreadMessages: number;
  quotationIdentifiers: any;
  grantId?: string;
  companyId?: string;
}

export interface EmailMessageRecipient {
  name: string;
  email: string;
}

export interface EmailMessageFile {
  id: string;
  size: number;
  fileUrl: string;
  filename: string,
  content_type: string,
  content_disposition: "attachment" | "inline",
  content_id?: string,
}

export enum PhoneEventType {
  VOICE_CALL_ANSWERED = 'voice_call_answered',
  VOICE_CALL_OUT_OF_SERVICE = 'voice_call_out_of_service',
  VOICE_CALL_IN_TALK = 'voice_call_in_talk',
  VOICE_CALL_MISS = 'voice_call_miss',
  VOICE_CALL_AGENT_TRY = 'voice_call_agent_try',
  ACTION_SET_VOICE_CALL_INFORMATION = 'action_set_voice_call_information',
}

export interface PhoneContent {
  human?: PhoneHuman;
  eventType: PhoneEventType;
  eventTypeReason: string;
  voiceCall: PhoneVoiceCall;
  voiceCallChannel: {
    id: string;
    source: string;
  };
  observations?: string
}

export interface PhoneHuman {
  name?: string;
  userId: number;
}

export interface PhoneVoiceCall {
  url: string;
  duration: string;
}

export enum PhoneIcon {
  VOICE_CALL_ANSWERED = 'call_end',
  VOICE_CALL_IN_TALK_TRAVALER = 'phone_enabled',
  VOICE_CALL_IN_TALK = 'phone_callback',
  VOICE_CALL_MISS = 'phone_missed',
  VOICE_CALL_AGENT_TRY = 'phone_in_talk',
  TRANSFER_CALL = 'phone_forwarded',
}

export type QuotationData = {
  id: number,
  uuid: string,
  adults: number,
  guests: any,
  source: any,
  userId: number,
  idHotel: string,
  wasSend: boolean,
  children: number,
  searched: boolean,
  childAges: [number],
  companyId: string,
  createdAt: number,
  nlpSearch: boolean,
  totalRooms: number,
  arrivalDate: number,
  arrivalTime: any,
  errorResult: any,
  maximumPrice: any,
  minimumPrice: any,
  bookingEngine: string,
  departureDate: number,
  departureTime: any,
  conversationId: string,
  bookingDiscount: any,
  foundAvailability: boolean,
  reservationIntegration: boolean,
  reservationIntegrationVerifiedAt: any,
  sophiaQuote?: boolean
}
